import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // If returning from Google OAuth, exchange the session_id FIRST before the /auth/me check.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
      api
        .post("/auth/google/session", { session_id: sid })
        .then(({ data }) => setUser(data.username))
        .catch(() => setUser(null))
        .finally(() => {
          // strip the fragment so it can't be reused / re-processed
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          setLoading(false);
        });
      return;
    }
    api
      .get("/auth/me", { timeout: 8000 })
      .then(({ data }) => setUser(data.username))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    setUser(data.username);
    return data;
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.username);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore network errors on logout */
    }
    localStorage.removeItem("hdg_ws");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, loginWithGoogle }),
    [user, loading, login, register, logout, loginWithGoogle]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
